import { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useParams } from "react-router-dom";
import { CONFIG } from "../config";
import Loading from './loading/Loading';
import axios from '../utils/axios';
import ProductCard from './ProductCard';
import SearchPanel from './SearchPanel';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import jalaliday from "jalaliday";
import "dayjs/locale/fa";
import "../css/Gallery.css";

const Page = () => {
  const [products, setProducts] = useState([]);
  const { user, isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [modalIndex, setModalIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchDropdown, setSearchDropdown] = useState(false);
  const [setlang, setLang] = useState("fa");
  const { lang } = useParams();
  const currentUser = user;
  const API_BASE = CONFIG.API_URL;

  // Filters states (these are already wired to SearchPanel)
  const [searchAll, setSearchAll] = useState('');
  const [searchNameDesc, setSearchNameDesc] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchMaterial, setSearchMaterial] = useState('');
  const [searchAuthorName, setSearchAuthorName] = useState('');
  const [searchStyle, setSearchStyle] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const PSize = 24;



  // Fetch all products across pages with backend search applied
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
  
    try {
      let url = `${API_BASE}/api/listview/?page_size=${PSize}`;
      const params = {};
  
      if (searchAll) params.search = searchAll;  
      if (searchNameDesc) params.namedesc = searchNameDesc;  
      if (searchCategory) params.category = searchCategory;
      if (searchMaterial) params.material = searchMaterial;
      if (searchAuthorName) params.artist = searchAuthorName;
      if (searchStyle) params.style = searchStyle;
      if (priceMin) params.price_min = priceMin;
      if (priceMax) params.price_max = priceMax;
  
      let allProducts = [];
      let nextUrl = url;
  
      while (nextUrl) {
        const response = await axios.get(nextUrl, { params, withCredentials: false });
        const data = response.data;
  
        if (Array.isArray(data.results)) {
          allProducts = [...allProducts, ...data.results];
        }
        nextUrl = data.next;
      }
  
      setProducts(allProducts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false); 
    }
  };
  
  useEffect(() => {
    fetchProducts();
  }, []); 

  const handleSearch = () => {
    setCurrentPage(1);
    fetchProducts();
  };


  dayjs.extend(utc);
  dayjs.extend(jalaliday);

  useEffect (() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if(lang !== "fa" && lang !== "en") {
      if ( userTimezone === "Asia/Tehran") {
        setLang(`fa`);
      } else {
        setLang(`en`);
      }
    } else {
      setLang(lang)
    }
  }, [lang])

  // Pagination logic (frontend, after fetching all items)
  const totalPages = Math.ceil(products.length / PSize);
  const currentProducts = products.slice((currentPage - 1) * PSize, currentPage * PSize);

  const goToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
    setModalIndex(null);
  };

  const openModal = (index) => setModalIndex(index);
  const closeModal = () => setModalIndex(null);
  const goToNextProduct = () => {
    if (modalIndex < currentProducts.length - 1) setModalIndex(modalIndex + 1);
  };
  const goToPreviousProduct = () => {
    if (modalIndex > 0) setModalIndex(modalIndex - 1);
  };

  if (loading) return <Loading/>;

  return (
    <div  className='GalleryPage'>
      <SearchPanel
        searchAll={searchAll}
        setSearchAll={setSearchAll}
        searchNameDesc={searchNameDesc}
        setSearchNameDesc={setSearchNameDesc}
        searchCategory={searchCategory}
        setSearchCategory={setSearchCategory}
        searchMaterial={searchMaterial}
        setSearchMaterial={setSearchMaterial}
        searchStyle={searchStyle}
        setSearchStyle={setSearchStyle}
        searchAuthorName={searchAuthorName}
        setSearchAuthorName={setSearchAuthorName}
        priceMin={priceMin}
        setPriceMin={setPriceMin}
        priceMax={priceMax}
        setPriceMax={setPriceMax}
        onSearch={handleSearch}
        searchDropdown={setSearchDropdown}
      />

      {products.length > 0 ? (
        <>
          <div className="av-glow-field" aria-hidden="true">
            <div className="av-grain" />
          </div>
          
      
          <div className="product-grid">
            {currentProducts.map((product, idx) => (
              <ProductCard
                isAuthenticated={isAuthenticated}
                user={user}
                currentUser={currentUser}
                key={product.id}
                productId={product.id}
                product={product}
                ownerId={product.owner_id}
                ownerUsername={product.owner}
                name={product.name}
                description={product.description}
                artist={product.artist}
                style={product.style}
                category={product.category}
                material={product.material}
                feature_one={product.feature_one}
                feature_two={product.feature_two}
                length={product.length}
                width={product.width}
                height={product.height}
                weight={product.weight}
                date={new Date(product.created_at).toLocaleDateString()}
                date_pa={dayjs.utc(product.created_at).calendar("jalali").locale("fa").format("YYYY/MM/DD")}
                delivered_by={product.delivered_by}
                pickup_address={product.pickup_address}
                price={product.price}
                bidzone_stored={product.bidzone_stored}
                is_biddable={product.is_biddable}
                b_deadline={product.bidding_deadline}
                b_deadline_pa={dayjs.utc(product.bidding_deadline).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}
                bid_amount={product.bid_amount}
                bidder={product.bidder}
                images={product.images}
                videos={product.videos}
                isModalOpen={modalIndex === idx}
                onOpenModal={() => openModal(idx)}
                onCloseModal={closeModal}
                onNext={goToNextProduct}
                onPrev={goToPreviousProduct}
                isFirst={modalIndex === 0}
                isLast={modalIndex === currentProducts.length - 1}
              />
            ))}
          </div>

          {/* Pagination controls */}
          <div className="pagination">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              {setlang === "en" ? "Prev" : "قبلی" }
            </button>
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  style={{fontWeight: pageNum === currentPage ? 'bold' : 'normal'}}
                >
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              {setlang === "en" ? "Next" : "بعدی" }
            </button>
          </div>
        </>
      ) : (
        <div className="no-search-results">
          {setlang === "en" ?
            <p>No search results</p>:
            <p>هیچ آیتمی یافت نشد</p>
          }
        </div>
      )}
    </div>
  );
};

export default Page;
